import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import Animated, { useAnimatedScrollHandler, useSharedValue, } from 'react-native-reanimated';
import { Keyboard, StyleSheet, View } from 'react-native';
import { rem, AppView, SettingsSection, NavigationHeader, SettingsItemInput, SCROLL_INDICATOR_INSETS, Button, } from 'design-system';
import StatusBar from 'design-system/StatusBar';
import { IconPostageStamp } from 'design-system/icons';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Amplitude from 'utils/amplitude';
import useColors from 'hooks/useColors';
import { focus, useAutoFocusInput } from 'hooks/useAutoFocusInput';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import { CLEAR_ADDRESS_LIST, getUserAddresses } from 'actions';
import { useFocusEffect } from 'hooks';
import { selectAddressToDisplay, selectIsLoggedIn, selectPrimaryAddressForGB, } from 'reducers/selectors';
import { OnboardingFlow } from 'utils/types/navigation';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { validateUKPostcode } from 'utils/validation';
import { KeyboardAvoidingContainerNew } from 'design-system/KeyboardAvoidingContainerNew';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import Modal from 'utils/packages/Modal';
import { setPendingPreviousAddress } from 'features/credit-score/reducers/creditScoreOnboarding';
import { setMonevoNoPreviousAddress } from 'features/borrow/monevo/reducers/monevoOnboarding';
import useMonevoNavigation from 'features/borrow/monevo/hooks/useMonevoNavigation';
import { popToOrNavigate } from 'utils/navigationv6';
import useCreditScoreRegistration from 'features/credit-score/hooks/useCreditScoreRegistration';
import LeaveOnboardingButton from 'features/wealth/components/LeaveOnboardingButton';
import { useNavigateIfAddressInLoginNav } from '../hooks/useNavigateIfAddressInLoginNav';
import { getAmplitudeFlow } from './utils';
const AddressPostCodeScreen = ({ navigation, route, }) => {
    const scrollY = useSharedValue(0);
    const colors = useColors();
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const { register } = useCreditScoreRegistration(true);
    const { navigateToNextStep } = useMonevoNavigation();
    const postCodeRef = useRef(null);
    const isLoggedIn = useAppSelector(selectIsLoggedIn);
    const addressToDisplay = useAppSelector(selectAddressToDisplay);
    const { isAddingSecondaryAddress, flow, mountedOnAnotherScreen } = route.params || {};
    const [postCode, setPostCode] = React.useState('');
    useEffect(() => {
        if (!mountedOnAnotherScreen) {
            Amplitude.logEvent('User Postcode Screen', {
                flow: getAmplitudeFlow(flow),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const getArticlesKey = (flow) => {
        switch (flow) {
            case OnboardingFlow.RentReporting:
                return HELP_ARTICLES_KEY.RENT_REPORTING_ONBOARDING;
            case OnboardingFlow.CreditCard:
                return HELP_ARTICLES_KEY.LOAN_ONBOARDING;
            case OnboardingFlow.Pots:
                return HELP_ARTICLES_KEY.POTS_ONBOARDING;
            default:
                return undefined;
        }
    };
    const renderHelpButton = useCallback(() => <HelpScreenButton articlesKey={getArticlesKey(flow)}/>, [flow]);
    useEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.elements.primary,
        });
    }, [colors.elements.primary]);
    useEffect(() => {
        /**
         * This call is to fetch userAddresses during loginNav
         */
        if (!isLoggedIn && !route.params?.skipGetAddress) {
            dispatch(getUserAddresses());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useNavigateIfAddressInLoginNav();
    const clearAddressList = useCallback(() => {
        dispatch({
            type: CLEAR_ADDRESS_LIST,
        });
    }, []);
    useFocusEffect(clearAddressList);
    const onSetPostCode = useCallback((postcode) => {
        setPostCode(postcode.toUpperCase());
    }, []);
    const clearPostCode = useCallback(() => {
        setPostCode('');
    }, []);
    const onPressLivedOutsideUK = useCallback(() => {
        Keyboard.dismiss();
        Modal.showConfirmation({
            title: 'Did you live in the UK before going abroad?',
            subtitle: 'To check your credit report, we need your previous UK address, even if you spent time living overseas in between',
            confirmButtonTitle: 'Enter previous UK address',
            onConfirm: () => focus(postCodeRef),
            cancelButtonTitle: 'I’ve never lived in the UK before',
            onPressCancel: () => {
                if (flow === OnboardingFlow.CreditCard) {
                    dispatch(setMonevoNoPreviousAddress());
                    navigateToNextStep('addPreviousAddress');
                    return;
                }
                dispatch(setPendingPreviousAddress(undefined));
                const isReviewKYCAddressInStack = navigation
                    .getState()
                    .routes.some((r) => r.name === 'CreditScoreReviewKYCAddress');
                const primaryAddress = selectPrimaryAddressForGB(store.getState());
                if (isReviewKYCAddressInStack &&
                    isAddingSecondaryAddress &&
                    primaryAddress) {
                    register(primaryAddress.id, undefined);
                    return;
                }
                popToOrNavigate(navigation, 'CreditScoreRegistration');
            },
        });
    }, [flow, isAddingSecondaryAddress, navigateToNextStep, register]);
    const onSubmitEditing = useCallback(() => {
        Keyboard.dismiss();
        const trimmedPostCode = postCode.trim();
        const postcodes = validateUKPostcode(trimmedPostCode);
        if (!trimmedPostCode || !postcodes) {
            Modal.showConfirmation({
                title: 'Invalid postcode',
                subtitle: 'Please enter a vlaid UK postcode so we can find your address',
                confirmButtonTitle: 'Okay',
            });
            return;
        }
        if (route.params && 'childDetails' in route.params) {
            navigation.navigateDeprecated('ChooseAddressScreen', {
                flow: route.params.flow,
                postCode: trimmedPostCode,
                accountId: route.params.accountId,
                childDetails: route.params.childDetails,
            });
        }
        else {
            navigation.navigateDeprecated('ChooseAddressScreen', {
                postCode: trimmedPostCode,
                flow: route.params?.flow,
                isAddingSecondaryAddress,
            });
        }
    }, [isAddingSecondaryAddress, postCode, route.params]);
    useAutoFocusInput(postCodeRef);
    const screenTitle = useMemo(() => {
        if (flow === OnboardingFlow.JISA) {
            return 'What’s your child’s current permanent address?';
        }
        if (flow === OnboardingFlow.Pots ||
            flow === OnboardingFlow.Invest ||
            flow === OnboardingFlow.Wallet) {
            return 'What’s your current permanent address?';
        }
        if (flow === OnboardingFlow.CreditScore) {
            if (isAddingSecondaryAddress) {
                return 'What was your previous address?';
            }
            return 'What’s your permanent address?';
        }
        if (isAddingSecondaryAddress) {
            return 'What’s your previous address?';
        }
        if (addressToDisplay) {
            return 'What’s your new permanent\xa0address?';
        }
        return 'What’s your permanent\xa0address?';
    }, [addressToDisplay, flow, isAddingSecondaryAddress]);
    const screenDesc = useMemo(() => {
        if (flow === OnboardingFlow.Pots ||
            flow === OnboardingFlow.Invest ||
            flow === OnboardingFlow.Wallet) {
            return 'Please provide accurate information, you might need to verify this later';
        }
        if (flow === OnboardingFlow.CreditScore) {
            return isAddingSecondaryAddress
                ? 'Providing your address history helps us to find your credit report'
                : 'Help us find your report by providing the information';
        }
        return 'We are required to ask this information for regulatory purposes';
    }, [flow, isAddingSecondaryAddress]);
    const outputColors = useMemo(() => [colors.background.dark, colors.cards.onDark], [colors.background.dark, colors.cards.onDark]);
    const scrollHandler = useAnimatedScrollHandler((event) => {
        scrollY.value = event.contentOffset.y;
    });
    const { paddingHorizontalStyle } = useAppFrameDimensions(!!mountedOnAnotherScreen);
    const paddingBottom = useMarginBottom('paddingBottom', rem(56 + 16), paddingHorizontalStyle);
    return (<AppView withFrame={false}>
      <StatusBar barStyle="dark-content" animated/>
      <NavigationHeader scrollY={scrollY} hideTitleOnScroll regularTitleHeight outputColors={outputColors} isInTab={!!mountedOnAnotherScreen} leftContent={mountedOnAnotherScreen ? (<LeaveOnboardingButton flow={flow}/>) : undefined} rightContent={<View style={!!mountedOnAnotherScreen && styles.flexEnd}>
            {renderHelpButton()}
          </View>} title=""/>
      <KeyboardAvoidingContainerNew flexContainer withMarginHorizontal={false} isInTab={!!mountedOnAnotherScreen && !paddingHorizontalStyle}>
        <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={paddingBottom}>
          <ScreenInfoHeader title={screenTitle} subTitle={screenDesc}/>
          <SettingsSection noMarginTop>
            <SettingsItemInput maxLength={12} value={postCode} ref={postCodeRef} testID="PostCodeInput" placeholder="Add..." returnKeyType="search" description="Postcode" autoCapitalize="characters" icon={<IconPostageStamp />} onChangeText={onSetPostCode} onClearTextInput={clearPostCode} onSubmitEditing={onSubmitEditing} autoComplete="postal-code"/>
          </SettingsSection>
        </Animated.ScrollView>
      </KeyboardAvoidingContainerNew>
      <KeyboardAvoidingContainerNew sticky withFrame isInTab={!!mountedOnAnotherScreen}>
        {(flow === OnboardingFlow.CreditScore ||
            flow === OnboardingFlow.CreditCard) &&
            isAddingSecondaryAddress ? (<Button custom onPress={onPressLivedOutsideUK} title="I lived outside the UK" textColor={colors.text.brand}/>) : null}
        <Button brand title={flow === OnboardingFlow.JISA ? 'Find address' : 'Find my address'} onPress={onSubmitEditing} disabled={!postCode}/>
      </KeyboardAvoidingContainerNew>
    </AppView>);
};
export default AddressPostCodeScreen;
const styles = StyleSheet.create({
    flexEnd: {
        paddingRight: rem(16),
        marginBottom: rem(6),
        alignItems: 'flex-end',
    },
});
